<template>
  <div class="combat-encounter-info">
    <template v-if="combatant">
      <div class="t-24 fw-6">{{ combatant.name }}</div>
      <div class="t-12 m-tx1">
        <span>{{ combatant.size }} {{ combatant.subtype || combatant.type }}, {{ combatant.alignment }}</span>
      </div>

      <div class="separator bg-dark-gray m-tx2 m-bx2"></div>
      <div>
        <div v-for="armorClass in combatant.armor_class" class="t">
          <span class="fw-7">Armor Class:</span>
          <span>&nbsp;{{ armorClass.value }} ({{ armorClass.type }})</span>
        </div>
        <div class="t m-t4">
          <span class="fw-7">Hit Points:</span>
          <span>&nbsp;{{ combatant.hit_points }} ({{ combatant.hit_points_roll }})</span>
        </div>
        <div v-for="type in Object.keys(combatant.speed)" class="t m-t4">
          <span class="fw-7">{{ type.charAt(0).toUpperCase() + type.slice(1) }} Speed:</span>
          <span >&nbsp;{{ combatant.speed[type] }}</span>
        </div>
      </div>

      <div class="separator bg-dark-gray m-tx2 m-bx2"></div>
      <div class="f-sb w-100p m-tx2">
        <button v-for="stat in stats" class="button w-x16 h-x12 p-x1 bg-dark-gray f-jc f-ac f-v f-sb" @click="rollDice(`1d20 + ${statModifier(stat)}`, statShorthand(stat))">
          <div class="t-18 white">{{ statShorthand(stat) }}</div>
          <div class="t-14 white">
            {{ combatant[stat] }} [{{ (statModifier(stat) >= 0 ? '+' : '') + statModifier(stat) }}]
          </div>
        </button>
      </div>

      <div class="separator bg-dark-gray m-tx2 m-bx2"></div>
      <div>
        <div v-if="skills.length" class="f-w f-ac">
          <div class="t fw-7">Skills:</div>
          <button
            v-for="skill in skills"
            class="button bg-dark-gray white h-x6 t-10 m-lx1"
            @click="rollDice(`1d20 + ${skill.value}`, skill.name)"
          >{{ skill.name }} +{{ skill.value }}</button>
        </div>
        <div v-if="senses.length" class="t m-tx1">
          <span class="fw-7">Senses:</span>
          <span>&nbsp;{{ senses.join(', ') }}</span>
        </div>
        <div v-if="combatant.languages" class="t m-tx1">
          <span class="fw-7">Languages:</span>
          <span>&nbsp;{{ combatant.languages }}</span>
        </div>
        <div v-if="combatant.challenge_rating && combatant.xp" class="t m-tx1">
          <span class="fw-7">Challenge:</span>
          <span>&nbsp;{{ combatant.challenge_rating }} ({{ combatant.xp }}XP)</span>
        </div>
        <div v-if="combatant.proficiency_bonus" class="t m-tx1">
          <span class="fw-7">Proficiency Bonus:</span>
          <span>&nbsp;{{ combatant.proficiency_bonus }}</span>
        </div>
      </div>

      <template v-if="combatant.special_abilities">
        <div class="separator bg-dark-gray m-tx2 m-bx2"></div>
        <div class="t-20 fw-6">Traits</div>
        <div v-for="ability in combatant.special_abilities" class="m-tx2">
          <div class="t">
            <span class="fw-7">{{ ability.name }}:</span>
            <span>&nbsp;{{ ability.desc }}</span>
          </div>
          <div v-if="detectDiceRolls(ability.desc)?.length" class="f-w f-ac m-lx4 m-tx1">
            <div class="t fw-6">Dice Rolls:</div>
            <button
              v-for="dice in detectDiceRolls(ability.desc)"
              class="button bg-dark-gray white h-x6 t-10 m-lx1"
              @click="rollDice(dice, ability.name)"
            >{{ dice }}</button>
          </div>
        </div>
      </template>

      <template v-if="combatant.actions">
        <div class="separator bg-dark-gray m-tx2 m-bx2"></div>
        <div class="t-20 fw-6">Actions</div>
        <div v-for="action in combatant.actions" class="m-tx2">
          <div class="t">
            <span class="fw-7">{{ action.name }}:</span>
            <span>&nbsp;{{ action.desc }}</span>
          </div>
          <div v-if="diceRollsForAction(action).length" class="f-w f-ac m-lx4 m-tx1">
            <div class="t fw-6">Dice Rolls:</div>
            <button
              v-for="roll in diceRollsForAction(action)"
              class="button bg-dark-gray white h-x6 t-10 m-lx1"
              @click="rollDice(roll.dice, roll.subtitle)"
            >{{ roll.buttonText }}</button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
  import { computed } from 'vue';
  import { useCombatEncounterStore } from '../../stores/combat_encounter_store';

  const store = useCombatEncounterStore();

  const combatant = computed(() => store.inspectedCombatant);
  
  // Stats
  const stats = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
  const statShorthand = (stat) => { return stat.slice(0, 3).toUpperCase() };
  const statModifier = (stat) => { return Math.floor((combatant.value[stat] - 10) / 2.0) };

  // Skills
  const skills = computed(() => {
    const skills = [];
    combatant.value.proficiencies.forEach((prof) => {
      const name = prof.proficiency.name.split('Skill: ')[1];
      if (name) skills.push({ name, value: prof.value });
    });
    return skills;
  });

  // Senses
  const senses = computed(() => {
    return Object.keys(combatant.value.senses).map((sense) => {
      const name = sense.split('_').map(str => str.charAt(0).toUpperCase() + str.slice(1)).join(' ');
      const value = combatant.value.senses[sense];
      return name + ' ' + value;
    });
  });

  // Actions
  const diceRollsForAction = (action) => {
    const rolls = [];
    if (action.attack_bonus) {
      rolls.push({
        dice: `1d20 + ${action.attack_bonus}`,
        subtitle: `${action.name} - Att.`,
        buttonText: `Attack (+${action.attack_bonus})`
      });
    };
    action.damage?.forEach((damage) => {
      if (damage.damage_dice) {
        rolls.push({
          dice: damage.damage_dice,
          subtitle: `${action.name} - ${damage.damage_type.name} Dmg.`,
          buttonText: `${damage.damage_type.name} Dmg. (${damage.damage_dice})`,
        })
      };
      damage.from?.options?.forEach((option) => {
        rolls.push({
          dice: option.damage_dice,
          subtitle: `${action.name} - ${option.damage_type.name} Dmg.`,
          buttonText: `${option.damage_type.name} Dmg. (${option.damage_dice})`,
        });
      });
    });
    if (!rolls.length) {
      // There may still be rolls not officially in the damage section, scan the text
      detectDiceRolls(action.desc)?.forEach((roll) => {
        rolls.push({
          dice: roll,
          subtitle: `${action.name} - Detected Roll`,
          buttonText: roll,
        })
      });
    }
    return rolls;
  };

  // Dice Rolls
  const rollDice = (dice, subtitle) => {
    store.rollDice({ dice, title: `${combatant.value.name}: ${subtitle}` });
  }
  const detectDiceRolls = (phrase) => {
    const diceRollRegex = /\b(\d*)d(\d+)(?:\s*([+-])\s*(\d+))?\b/g;
    return phrase.match(diceRollRegex);
  };
</script>